import { useState } from 'react'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber, fmtPercent } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { analyticsCrossTab } from '../api'
import type { CrossTabResponse, TargetGroupsResponse } from '@/shared/api/types'
import { MeasuredWith, Money, Pct, Pp, Truncation, Unmatchable, Value } from '../parts'
import { EnvelopeBar } from '../parts'
import { analyticsTargetGroups } from '../api'
import { AnalyticsScreen, useAnalyticsFilters } from './shell'

/**
 * Who the portfolio aimed at, by target group.
 *
 * ## The same programme is in three rows
 *
 * A group is a saved rule, not a partition. A youth bursary for women in Kisumu
 * belongs to YOUTH, to WOMEN and to a county group all at once, so the rows do
 * not sum to the portfolio. Both totals are printed — `deduplicatedTotals`
 * counts that programme once, `sumOfRows` counts it three times — because the
 * second one is what a reader gets if they add the column up themselves, and
 * they will.
 *
 * ## A group with no eligible pool is not a group with none eligible
 *
 * `eligiblePool` is null when the group's rule has no computable denominator —
 * typically a rule on a tag nobody carries. Coverage is then unavailable, not
 * zero, and the reason travels with the cell.
 */
export function ProgramTargetGroupsScreen() {
  return (
    <AnalyticsScreen
      title="Target groups"
      description="The saved rules programmes aimed at, and how far each one reached."
      load={(f) => analyticsTargetGroups(f)}
      isEmpty={(d) => (d.rows?.length ?? 0) === 0}
      exportKind="TARGET_GROUPS"
    >
      {(d) => <TargetGroupsBody d={d} />}
    </AnalyticsScreen>
  )
}

function TargetGroupsBody({ d }: { d: TargetGroupsResponse }) {
  const [showCrossTab, setShowCrossTab] = useState(false)
  const rows = d.rows ?? []
  const dedup = d.deduplicatedTotals
  const sum = d.sumOfRows

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard
          label="Groups"
          value={fmtNumber(rows.length)}
          sub="Saved rules with at least one programme"
        />
        <MetricCard
          label="Programmes"
          value={fmtNumber(dedup?.programmes ?? 0)}
          accent
          sub={(
            <span title="A programme in three groups is one programme. Adding the rows gives the larger figure.">
              rows add to {fmtNumber(sum?.programmes ?? 0)}
            </span>
          )}
        />
        <MetricCard
          label="Beneficiaries"
          value={fmtNumber(dedup?.beneficiaries ?? 0)}
          sub={`rows add to ${fmtNumber(sum?.beneficiaries ?? 0)}`}
        />
        <MetricCard
          label="Budget"
          value={<Money value={dedup?.budget} currency={dedup?.currency} mixedReason={dedup?.currencyMixedReason} />}
          sub={<>delivered <Money value={dedup?.delivered} currency={dedup?.currency} mixedReason={dedup?.currencyMixedReason} /></>}
        />
      </KpiStrip>

      <Unmatchable count={d.unmatchableSubjects} />

      <Note tone="info">
        Groups overlap by design. Every figure on the strip above appears twice — once
        counting each programme one time, and once as the rows would add up. Neither is
        wrong; they answer different questions.
      </Note>

      <Panel
        title="Groups"
        bodyClassName="p-0"
        right={(
          <button type="button" className="erp-btn" onClick={() => setShowCrossTab((v) => !v)}>
            {showCrossTab ? 'Hide' : 'Show'} group × unit
          </button>
        )}
      >
        <ErpTable head={(
          <>
            <Th>Group</Th>
            <Th className="text-right">Programmes</Th>
            <Th className="text-right">Beneficiaries</Th>
            <Th className="text-right">Eligible</Th>
            <Th className="text-right">Coverage</Th>
            <Th className="text-right">Budget</Th>
            <Th className="text-right">Delivered</Th>
            <Th className="text-right">Mean uplift</Th>
            <Th>Top programmes</Th>
          </>
        )}>
          {rows.length === 0
            ? <EmptyRow colSpan={9} message="No target group has a programme under this filter." />
            : rows.map((r) => (
              <tr key={r.groupKey}>
                <Td>
                  <div className="font-medium">{r.displayName}</div>
                  {r.requiresTag?.length > 0 && (
                    <div className="text-erp-xs text-muted-foreground">
                      needs tag {r.requiresTag.join(', ')}
                    </div>
                  )}
                </Td>
                <Td className="text-right tabular-nums">
                  {fmtNumber(r.programmes)}
                  <span className="text-muted-foreground"> · {fmtNumber(r.activeProgrammes)} active</span>
                </Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.beneficiaries)}</Td>
                <Td className="text-right tabular-nums">
                  <Value value={r.eligiblePool} reason={r.eligibleUnavailableReason} shortReason />
                </Td>
                <Td className="text-right">
                  <Pct value={r.coverage} reason={r.eligibleUnavailableReason} shortReason />
                </Td>
                <Td className="text-right">
                  <Money value={r.budget} currency={r.currency} mixedReason={r.currencyMixedReason} />
                </Td>
                <Td className="text-right">
                  <Money value={r.delivered} currency={r.currency} mixedReason={r.currencyMixedReason} />
                </Td>
                <Td className="text-right">
                  <div><Pp value={r.meanUplift} reason="No post-enrolment reading in this group." shortReason /></div>
                  <MeasuredWith measured={r.measured} measurable={r.measurable} className="text-erp-xs text-muted-foreground" />
                </Td>
                <Td className="max-w-[16rem]">
                  <span className="truncate block text-erp-xs text-muted-foreground"
                    title={r.topProgrammes?.map((p) => p.name).join(', ')}>
                    {r.topProgrammes?.map((p) => p.name).join(', ') || '—'}
                  </span>
                </Td>
              </tr>
            ))}
        </ErpTable>
      </Panel>

      {showCrossTab && <CrossTabPanel />}
    </div>
  )
}

/**
 * Group × unit coverage.
 *
 * A matrix is the one place a sequential ramp earns its keep — the cell's job is
 * magnitude, there is exactly one measure, and position already carries both
 * identities. One hue, light to dark, and every cell keeps its number so the
 * shade is a shortcut rather than the only way to read it.
 */
function CrossTabPanel() {
  const f = useAnalyticsFilters()
  const { data, error, loading } = useLoad<CrossTabResponse>(
    () => analyticsCrossTab(f.query), [JSON.stringify(f.query)],
  )

  if (loading && !data) return <Panel title="Group × unit"><LoadingBlock label="Loading cross-tab…" /></Panel>
  if (error) return <Panel title="Group × unit"><ErrorStrip message={error} /></Panel>
  if (!data) return null

  const units = data.units ?? []
  const groups = data.groupKeys ?? []
  const byKey = new Map((data.cells ?? []).map((c) => [`${c.groupKey}|${c.unitId}`, c]))
  const peak = Math.max(
    ...(data.cells ?? []).map((c) => (c.coverage == null ? 0 : Number(c.coverage))),
    0.0001,
  )

  return (
    <Panel
      title="Group × unit coverage"
      description="Shade is coverage against this group's eligible pool in this unit. Every cell keeps its number."
      bodyClassName="p-0"
    >
      <div className="px-4 pt-3">
        <EnvelopeBar env={data} />
        <Truncation truncatedTo={data.truncatedTo} totalAvailable={data.totalAvailable}
          orderedBy={data.orderedBy} noun="cells" />
      </div>
      <ErpTable head={(
        <>
          <Th>Unit</Th>
          {groups.map((g) => <Th key={g} className="text-right">{g.replace(/_/g, ' ')}</Th>)}
        </>
      )}>
        {units.length === 0
          ? <EmptyRow colSpan={groups.length + 1} message="No units in scope." />
          : units.map((u) => (
            <tr key={u.unitId}>
              <Td title={u.path ?? undefined}>{u.name}</Td>
              {groups.map((g) => {
                const c = byKey.get(`${g}|${u.unitId}`)
                const cov = c?.coverage == null ? null : Number(c.coverage)
                return (
                  <Td key={g} className="text-right">
                    {c
                      ? (
                        <span
                          className={cn('inline-block rounded px-1.5 py-0.5 tabular-nums text-erp-xs',
                            cov === null && 'text-muted-foreground')}
                          style={cov === null ? undefined : {
                            // One hue. Opacity is the ramp step — no second colour is
                            // introduced, so the cell stays legible in both themes.
                            background: `color-mix(in oklab, var(--chart-1) ${Math.round((cov / peak) * 70) + 6}%, transparent)`,
                          }}
                          title={`${fmtNumber(c.enrolled)} enrolled${c.eligible != null ? ` of ${fmtNumber(c.eligible)} eligible` : ''}`}
                        >
                          {cov === null ? '—' : fmtPercent(cov, 0)}
                        </span>
                      )
                      : <span className="text-muted-foreground/50">·</span>}
                  </Td>
                )
              })}
            </tr>
          ))}
      </ErpTable>
      <p className="px-4 py-2 text-erp-xs text-muted-foreground border-t border-border">
        A dot means the group's rule produced no eligible people in that unit — not that
        nobody was enrolled there. An em dash means there were enrolments but no denominator.
      </p>
    </Panel>
  )
}
